import { type ChangeEvent, type FormEvent, useMemo, useState } from "react"
import styled from "styled-components"
import { useNavigate } from "react-router-dom"

import useAuthStore from "@features/auth/authStore.client"
import { ActionAnchor, ActionLink, ErrorNotice, PrimaryButton } from "@features/portal/PortalPrimitives"
import { serviceRequestsApi } from "@features/portal/serviceRequestsApi"
import { categoryLabel, type CreateServiceRequestPayload, type ServiceRequestCategory, type ServiceRequestPriority } from "@features/portal/types"
import PortalShell from "../Common/PortalShell"

const Stepper = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin: 0 0 1.5rem;
  padding: 0;
  list-style: none;
`

const Step = styled.li<{ $active: boolean; $complete: boolean }>`
  position: relative;
  min-width: 0;
  padding: 0.8rem 0.6rem;
  border-top: 3px solid ${({ $active, $complete }) => ($active || $complete ? "#337abd" : "#c5d0d9")};
  color: ${({ $active, $complete }) => ($active || $complete ? "#163d61" : "#768897")};
  font-size: 0.76rem;
  font-weight: ${({ $active }) => ($active ? 800 : 650)};

  span {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 1rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`

const FormCard = styled.form`
  min-width: 0;
  padding: clamp(1.35rem, 4vw, 2.5rem);
  border: 1px solid #c4d0da;
  border-radius: 2px;
  background: #ffffff;
  box-shadow: 0 18px 55px rgba(12, 44, 69, 0.06);

  h2 {
    margin: 0;
    color: #102d49;
    font-size: clamp(1.55rem, 4vw, 2.25rem);
    letter-spacing: -0.035em;
  }

  > p {
    margin: 0.8rem 0 1.5rem;
    max-width: 720px;
    color: #667b8d;
    line-height: 1.65;
  }
`

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const CategoryButton = styled.button<{ $selected: boolean }>`
  min-height: 82px;
  padding: 1rem;
  border: 1px solid ${({ $selected }) => ($selected ? "#337abd" : "#c4d0da")};
  border-left-width: ${({ $selected }) => ($selected ? "4px" : "1px")};
  border-radius: 2px;
  background: ${({ $selected }) => ($selected ? "#edf6fc" : "#ffffff")};
  color: #153751;
  text-align: left;
  font: inherit;
  font-weight: 750;
  cursor: pointer;

  &:hover {
    border-color: #6da0c7;
    background: #f5f9fc;
  }
`

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`

const Field = styled.label<{ $full?: boolean }>`
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
  display: grid;
  gap: 0.45rem;
  color: #173751;
  font-size: 0.86rem;
  font-weight: 750;

  input,
  textarea,
  select {
    width: 100%;
    min-height: 52px;
    padding: 0.8rem 0.9rem;
    border: 1px solid #b9c7d2;
    border-radius: 2px;
    background: #ffffff;
    color: #102c45;
    font: inherit;
    font-size: 0.95rem;
  }

  textarea {
    min-height: 150px;
    resize: vertical;
    line-height: 1.55;
  }

  input:focus,
  textarea:focus,
  select:focus {
    outline: 3px solid rgba(51, 122, 189, 0.12);
    border-color: #337abd;
  }
`

const Upload = styled.label`
  display: grid;
  place-items: center;
  min-height: 128px;
  margin-top: 1rem;
  padding: 1.25rem;
  border: 1px dashed #75a5c9;
  border-radius: 2px;
  background: #f3f8fb;
  color: #245d88;
  text-align: center;
  cursor: pointer;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 0.35rem;
    color: #6c8091;
    font-size: 0.78rem;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.8rem;

  figure {
    position: relative;
    margin: 0;
  }

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border: 1px solid #bdcad4;
  }

  button {
    position: absolute;
    top: 0.35rem;
    right: 0.35rem;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 0;
    background: rgba(7, 28, 48, 0.84);
    color: #ffffff;
    cursor: pointer;
  }
`

const Summary = styled.dl`
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  margin: 1.5rem 0;
  border-top: 1px solid #d0d9e1;

  dt,
  dd {
    margin: 0;
    padding: 0.85rem 0;
    border-bottom: 1px solid #d0d9e1;
  }

  dt {
    color: #667b8d;
    font-size: 0.8rem;
    font-weight: 750;
  }

  dd {
    color: #1c3c56;
    line-height: 1.55;
    white-space: pre-line;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;

    dt {
      padding-bottom: 0.2rem;
      border-bottom: 0;
    }
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1.5rem;
`

const SecondaryButton = styled(PrimaryButton)`
  border-color: #b9c7d2;
  background: transparent;
  color: #173751;

  &:hover:not(:disabled) {
    background: #edf3f7;
  }
`

const Urgency = styled.aside`
  align-self: start;
  padding: 1.4rem;
  border: 1px solid #c1ced8;
  border-top: 4px solid #c8773a;
  border-radius: 2px;
  background: #0a223a;
  color: #ffffff;

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }

  p {
    margin: 0.75rem 0 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.84rem;
    line-height: 1.6;
  }
`

const Success = styled.div`
  padding: clamp(2rem, 6vw, 5rem);
  border: 1px solid #bad0c2;
  border-top: 4px solid #4c9867;
  background: #ffffff;
  text-align: center;

  h2 {
    margin: 0;
    color: #143d29;
    font-size: clamp(2rem, 5vw, 3.5rem);
    letter-spacing: -0.045em;
  }

  p {
    max-width: 620px;
    margin: 1rem auto;
    color: #5d7265;
    line-height: 1.7;
  }
`

interface PreparedPhoto {
  file: File
  previewUrl: string
}

const compressImage = (file: File): Promise<PreparedPhoto> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error("Lecture impossible"))
    reader.onload = () => {
      const image = new Image()
      image.onerror = () => reject(new Error("Image invalide"))
      image.onload = () => {
        const maxSide = 1500
        let scale = Math.min(1, maxSide / Math.max(image.width, image.height))
        const canvas = document.createElement("canvas")
        const context = canvas.getContext("2d")
        if (!context) return reject(new Error("Compression impossible"))

        canvas.width = Math.max(1, Math.round(image.width * scale))
        canvas.height = Math.max(1, Math.round(image.height * scale))
        context.fillStyle = "#ffffff"
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression impossible"))
            const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
              type: "image/jpeg",
              lastModified: Date.now(),
            })
            resolve({ file: compressedFile, previewUrl: URL.createObjectURL(blob) })
          },
          "image/jpeg",
          0.78,
        )
      }
      image.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  })

const NewServiceRequest = () => {
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const [preparedPhotos, setPreparedPhotos] = useState<PreparedPhoto[]>([])
  const [payload, setPayload] = useState<CreateServiceRequestPayload>({
    propertyId: user?.propertyIds?.[0],
    propertyLabel: user?.unitAddress || "",
    unit: "",
    category: "BRIS",
    priority: "NORMAL",
    description: "",
    availability: "",
  })

  const categories = Object.entries(categoryLabel) as [ServiceRequestCategory, string][]
  const canContinue = useMemo(() => {
    if (step === 1) return Boolean(payload.category)
    if (step === 2) return payload.propertyLabel.trim().length >= 3 && payload.description.trim().length >= 20
    return true
  }, [payload, step])

  const setField = <K extends keyof CreateServiceRequestPayload>(key: K, value: CreateServiceRequestPayload[K]) => {
    setPayload((current) => ({ ...current, [key]: value }))
  }

  const addPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (!files.length) return
    if (preparedPhotos.length + files.length > 3) {
      setError("Vous pouvez joindre un maximum de 3 photos.")
      return
    }
    if (files.some((file) => file.size > 10 * 1024 * 1024)) {
      setError("Chaque photo doit faire moins de 10 Mo avant compression.")
      return
    }
    setError("")
    try {
      const compressed = await Promise.all(files.map(compressImage))
      setPreparedPhotos((current) => [...current, ...compressed])
    } catch {
      setError("Une photo n’a pas pu être préparée. Utilisez une image JPG, PNG ou WebP.")
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (step < 3) {
      if (canContinue) setStep((current) => current + 1)
      return
    }
    setSubmitting(true)
    setError("")
    try {
      setUploading(preparedPhotos.length > 0)
      const attachments = await Promise.all(preparedPhotos.map((photo) => serviceRequestsApi.uploadAttachment(photo.file)))
      await serviceRequestsApi.create({ ...payload, attachments })
      preparedPhotos.forEach((photo) => URL.revokeObjectURL(photo.previewUrl))
      setSubmitted(true)
    } catch {
      setError("La demande n’a pas pu être transmise. Vérifiez votre connexion et réessayez.")
    } finally {
      setUploading(false)
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <PortalShell title="Demande transmise" eyebrow="Confirmation">
        <Success>
          <h2>Nous l’avons bien reçue.</h2>
          <p>La demande apparaît maintenant dans votre suivi. L’équipe pourra vous répondre dans le même dossier si une précision est nécessaire.</p>
          <ActionLink to="/app/demandes">Voir le suivi</ActionLink>
        </Success>
      </PortalShell>
    )
  }

  return (
    <PortalShell title="Nouvelle demande" eyebrow="En quelques étapes">
      <Stepper aria-label="Progression de la demande">
        {["Type", "Détails", "Vérification"].map((label, index) => (
          <Step key={label} $active={step === index + 1} $complete={step > index + 1}>
            <span>Étape {index + 1}</span>
            {label}
          </Step>
        ))}
      </Stepper>

      <Workspace>
        <FormCard onSubmit={submit}>
          {step === 1 && (
            <>
              <h2>Quelle est la raison de votre demande?</h2>
              <p>Choisissez la catégorie la plus proche. L’équipe pourra l’ajuster au besoin.</p>
              <CategoryGrid>
                {categories.map(([value, label]) => (
                  <CategoryButton key={value} type="button" $selected={payload.category === value} onClick={() => setField("category", value)}>
                    {label}
                  </CategoryButton>
                ))}
              </CategoryGrid>
            </>
          )}

          {step === 2 && (
            <>
              <h2>Donnez-nous les détails utiles.</h2>
              <p>Une description précise et quelques photos permettent de mieux préparer l’intervention.</p>
              <Fields>
                <Field>
                  Adresse ou immeuble
                  <input
                    value={payload.propertyLabel}
                    onChange={(event) => setField("propertyLabel", event.target.value)}
                    placeholder="123, rue Exemple"
                    required
                  />
                </Field>
                <Field>
                  Unité ou appartement
                  <input value={payload.unit || ""} onChange={(event) => setField("unit", event.target.value)} placeholder="Ex. 4" />
                </Field>
                <Field>
                  Priorité
                  <select value={payload.priority} onChange={(event) => setField("priority", event.target.value as ServiceRequestPriority)}>
                    <option value="NORMAL">Normale</option>
                    <option value="PRIORITAIRE">Prioritaire</option>
                    <option value="URGENT">Urgente — appel requis si immédiat</option>
                  </select>
                </Field>
                <Field>
                  Disponibilités
                  <input
                    value={payload.availability || ""}
                    onChange={(event) => setField("availability", event.target.value)}
                    placeholder="Ex. lundi après 15 h"
                  />
                </Field>
                <Field $full>
                  Description
                  <textarea
                    value={payload.description}
                    onChange={(event) => setField("description", event.target.value)}
                    placeholder="Décrivez ce qui se passe, depuis quand et l’endroit exact…"
                    minLength={20}
                    maxLength={3000}
                    required
                  />
                </Field>
              </Fields>
              <Upload>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple capture="environment" onChange={addPhotos} />
                <strong>Ajouter des photos</strong>
                <span>Jusqu’à 3 images. Elles seront réduites avant l’envoi.</span>
              </Upload>
              {preparedPhotos.length > 0 && (
                <PhotoGrid>
                  {preparedPhotos.map((photo, index) => (
                    <figure key={`${photo.file.name}-${index}`}>
                      <img src={photo.previewUrl} alt={`Photo à joindre ${index + 1}`} />
                      <button
                        type="button"
                        aria-label={`Retirer la photo ${index + 1}`}
                        onClick={() => {
                          URL.revokeObjectURL(photo.previewUrl)
                          setPreparedPhotos((current) => current.filter((_, itemIndex) => itemIndex !== index))
                        }}
                      >
                        ×
                      </button>
                    </figure>
                  ))}
                </PhotoGrid>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <h2>Vérifiez avant l’envoi.</h2>
              <p>Ces renseignements seront visibles par l’équipe chargée du suivi.</p>
              <Summary>
                <dt>Catégorie</dt>
                <dd>{categoryLabel[payload.category]}</dd>
                <dt>Lieu</dt>
                <dd>
                  {payload.propertyLabel}
                  {payload.unit ? ` — unité ${payload.unit}` : ""}
                </dd>
                <dt>Priorité</dt>
                <dd>{payload.priority === "NORMAL" ? "Normale" : payload.priority === "PRIORITAIRE" ? "Prioritaire" : "Urgente"}</dd>
                <dt>Description</dt>
                <dd>{payload.description}</dd>
                <dt>Photos</dt>
                <dd>{preparedPhotos.length ? `${preparedPhotos.length} photo${preparedPhotos.length > 1 ? "s" : ""}` : "Aucune"}</dd>
              </Summary>
            </>
          )}

          {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
          <Actions>
            {step > 1 ? (
              <SecondaryButton type="button" onClick={() => setStep((current) => current - 1)}>
                Retour
              </SecondaryButton>
            ) : (
              <SecondaryButton type="button" onClick={() => navigate("/app")}>
                Annuler
              </SecondaryButton>
            )}
            <PrimaryButton type="submit" disabled={!canContinue || submitting}>
              {step < 3 ? "Continuer" : uploading ? "Téléversement des photos…" : submitting ? "Transmission…" : "Transmettre la demande"}
            </PrimaryButton>
          </Actions>
        </FormCard>

        <Urgency>
          <h2>Urgence immédiate?</h2>
          <p>
            En cas de dégât d’eau actif, de risque électrique, d’absence de chauffage en période froide ou de danger, appelez. Le formulaire ne
            remplace pas l’appel d’urgence.
          </p>
          <ActionAnchor href="tel:+14186849004">(418) 684-9004</ActionAnchor>
        </Urgency>
      </Workspace>
    </PortalShell>
  )
}

export default NewServiceRequest
